import { TZDate } from '@date-fns/tz';
import { addMonths, format, isValid, parse } from 'date-fns';

export const TIME_ZONE = 'Europe/Kyiv';

/** Календарна дата без часу, формат `yyyy-MM-dd` (так Postgres віддає тип `date`). */
export type IsoDate = string;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseIsoDate(value: IsoDate): Date {
  if (!ISO_DATE_RE.test(value)) throw new RangeError(`Invalid ISO date: ${value}`);
  const date = parse(value, 'yyyy-MM-dd', new Date(2000, 0, 1));
  if (!isValid(date)) throw new RangeError(`Invalid ISO date: ${value}`);
  return date;
}

export function isIsoDate(value: string): value is IsoDate {
  if (!ISO_DATE_RE.test(value)) return false;
  return isValid(parse(value, 'yyyy-MM-dd', new Date(2000, 0, 1)));
}

/**
 * Правило 5.1: дата завершення = дата оголошення + 1 місяць.
 * Число зберігається; якщо його немає в наступному місяці, береться останній день місяця.
 */
export function computeEndsAt(announcedAt: IsoDate): IsoDate {
  return format(addMonths(parseIsoDate(announcedAt), 1), 'yyyy-MM-dd');
}

/**
 * Правило 5.2: дострокове завершення = дата Протоколу №2, якщо вона раніше дати завершення.
 * ISO-дати порівнюються як рядки.
 */
export function computeClosedEarlyAt(
  protocol2Date: IsoDate | null | undefined,
  endsAt: IsoDate,
): IsoDate | null {
  if (!protocol2Date) return null;
  return protocol2Date < endsAt ? protocol2Date : null;
}

/** Сьогоднішня дата в Києві. */
export function todayKyiv(now: Date = new Date()): IsoDate {
  return format(new TZDate(now, TIME_ZONE), 'yyyy-MM-dd');
}

/** `yyyy-MM-dd` → `dd.MM.yyyy`. */
export function formatDate(value: IsoDate | null | undefined): string {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

/** Мітка часу → `dd.MM.yyyy HH:mm` за київським часом. */
export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '';
  return format(new TZDate(new Date(value), TIME_ZONE), 'dd.MM.yyyy HH:mm');
}

/** `dd.MM.yyyy` → `yyyy-MM-dd`, або null, якщо рядок не є коректною датою. */
export function parseUaDate(value: string): IsoDate | null {
  const date = parse(value.trim(), 'dd.MM.yyyy', new Date(2000, 0, 1));
  return isValid(date) ? format(date, 'yyyy-MM-dd') : null;
}
